import DataQuery from "data/DataQuery";
import { Customer_Invoices } from "data/endpoints";
import { useQuery } from "data/hooks/useQuery";
import InvoiceSimplified from "features/invoices/components/InvoiceSimplified";
import InvoiceSimplifiedHeader from "features/invoices/components/InvoiceSimplifiedHeader";
import InvoiceSimplifiedDTO from "features/invoices/dtos/InvoiceSimplifiedDTO";
import IInvoiceService from "features/invoices/services/IInvoiceService";
import IExternalFilterData from "features/shared/components/dataFilter/IExternalFilterData";
import DataFilterModal from "features/shared/components/dataFilterModal/DataFilterModal";
import DataGridHeader from "features/shared/components/dataGrid/DataGridHeader";
import DataGridRow from "features/shared/components/dataGrid/DataGridRow";
import { useStateDataGrid } from "features/shared/components/dataGrid/hooks/useStateDataGrid";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import Pagination from "features/shared/components/pagination/Pagination";
import SortingMenu from "features/shared/components/sorting/SortingMenu";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import { loadDataFilterConfig, loadDataSortConfig } from "features/shared/helpers/modules";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import CustomerState from "infrastructure/store/customer/CustomerState";
import IAppState from "infrastructure/store/IAppState";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const invoicesDataFilterJson = loadDataFilterConfig("contractualAccountDetailInvoicesDataFilter");
const invoicesDataSortJson = loadDataSortConfig("invoicesDataSort");
const pageSize = 10;

interface Props {
  contractualAccountId: string;
}

const ContractualAccountDetailInvoicesSection = ({ contractualAccountId }: Props) => {
  //#region DI
  const invoiceService = useInjection<IInvoiceService>(IocTypes.InvoiceService);
  //#endregion DI

  //#region store
  const customer = useSelector<IAppState, CustomerState>((store) => store.customer).customer;
  //#endregion store

  //#region state
  const [externalFilterData, setExternalFilterData] = useState<IExternalFilterData<any>[]>([]);
  //#endregion state

  //#region data grid hook
  const {
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    sortingDescriptors,
    paginationPageChangeHandler,
    filterApplyHandler,
    filterResetHandler,
    sortingChangeHandler,
    isApplyFilter,
    conditionalRefresh
  } = useStateDataGrid(pageSize, invoicesDataFilterJson, invoicesDataSortJson);
  //#endregion data grid hook

  //#region fetch data
  const getQueryParams = (): URLSearchParams => {
    let queryParams = new URLSearchParams(dataGrid.computedUrlSearchParams);
    queryParams.append("ContractualAccountId", contractualAccountId);

    return queryParams;
  };
  const query: DataQuery = {
    url: Customer_Invoices,
    params: getQueryParams(),
    routeParams: getRouteParams("customerId", customer.customerId!)
  };
  const { data: invoices, loadStatus: invoicesStatus } = useQuery<PagedDataResultDTO<InvoiceSimplifiedDTO>>(
    query,
    { count: 0, result: [] },
    [dataGrid.computedUrlSearchParams, customer.customerId],
    { useTracker: true },
    conditionalRefresh
  );
  //#endregion fetch data

  //#region side-effects
  useEffect(() => {
    getAndSetExternalFilterData();
  }, []);
  //#endregion side-effects

  //#region helpers
  const getAndSetExternalFilterData = async () => {
    let externalData = await invoiceService.getExternalFilterData([]);
    setExternalFilterData(externalData);
  }
  //#endregion helpers

  //#region sub-renders
  const renderInvoices = () => {
    return invoices.result.map((item) =>
      <DataGridRow key={item.invoiceId}>
        <InvoiceSimplified invoice={item} />
      </DataGridRow>
    );  
  } 

  const renderSortingMenu = () => { 
    return (
      <SortingMenu 
        id="invoices-sorting-menu"
        onSortingClick={sortingChangeHandler}
        descriptors={sortingDescriptors}
      />
    );
  }
  //#endregion sub-renders

  //#region render
  return (
    <NoContentWrapper
      data={invoices}
      loadStatus={invoicesStatus}
      noContentAsAlert
      isActiveFilter={isApplyFilter}
    >
      <DataGridHeader 
        count={invoices.count}
        setShowFilterModal={setShowFilter}
        filterActive={isApplyFilter()}
        renderSorting={renderSortingMenu}
      />
      <InvoiceSimplifiedHeader />
      {renderInvoices()}
      <div className="d-flex justify-content-center mt-4">
        <Pagination
          urlParams={new URLSearchParams(dataGrid.computedUrlSearchParams)}
          count={invoices.count!}
          pageSize={pageSize}
          onPageChange={paginationPageChangeHandler}
        />
      </div>

      <DataFilterModal
        descriptors={descriptors}
        externalData={externalFilterData}
        setDescriptors={setDescriptors}
        applyButtonOnClick={filterApplyHandler}
        resetButtonOnClick={filterResetHandler}
        show={showFilter}
        setShow={setShowFilter}
      />
    </NoContentWrapper>
  );
  //#endregion render
}

export default ContractualAccountDetailInvoicesSection;